import axios from 'axios';

// Create private axios instance with enhanced security
const apiPrivate = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000, // 30 seconds timeout
  withCredentials: true, // Include cookies for authentication
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': 'application/json',
  }
});

// Request interceptor for security and authentication
apiPrivate.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for security (only for authenticated requests)
    if (token) {
      config.headers['X-Request-Time'] = Date.now().toString();
      config.headers['X-Client-ID'] = localStorage.getItem('clientId') || generateClientId();
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor for security and error handling
apiPrivate.interceptors.response.use(
  (response) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }

    // Check for security headers
    if (response.headers['x-content-type-options'] !== 'nosniff') {
      console.warn('[Security Warning] Missing security headers');
    }

    return response;
  },
  (error) => {
    // Handle different types of errors securely
    if (error.response) {
      const { status, data } = error.response;
      
      // Log error details in development only
      if (import.meta.env.DEV) {
        console.error(`[API Error] ${status}`, data);
      }

      // Handle authentication errors
      if (status === 401) {
        // Clear invalid token
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        
        // Redirect to login if not already there
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        
        return Promise.reject({
          message: 'Authentication required',
          status: 401,
          redirect: true
        });
      }

      // Handle rate limiting
      if (status === 429) {
        return Promise.reject({
          message: data.message || 'Too many requests. Please try again later.',
          status: 429,
          retryAfter: data.retryAfter
        });
      }

      // Handle validation errors
      if (status === 400) {
        return Promise.reject({
          message: data.message || 'Invalid request data',
          status: 400,
          errors: data.errors || []
        });
      }

      // Handle server errors
      if (status >= 500) {
        return Promise.reject({
          message: 'Server error. Please try again later.',
          status: status,
          requestId: data.requestId
        });
      }

      // Generic error response
      return Promise.reject({
        message: data.message || 'An error occurred',
        status: status,
        data: data
      });
    }

    // Network or other errors
    if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        status: 0,
        networkError: true
      });
    }

    // Timeout errors
    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        message: 'Request timeout. Please try again.',
        status: 408,
        timeout: true
      });
    }

    // Generic error
    console.error('[API Error]', error);
    return Promise.reject({
      message: 'An unexpected error occurred',
      status: 0,
      originalError: error
    });
  }
);

// Generate unique client ID for tracking
function generateClientId() {
  const clientId = 'client_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  localStorage.setItem('clientId', clientId);
  return clientId;
}

// Security utilities
export const securityUtils = {
  // Sanitize data before sending
  sanitizeData: (data) => {
    if (typeof data === 'string') {
      return data
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '')
        .trim();
    }
    
    if (typeof data === 'object' && data !== null) {
      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        sanitized[key] = securityUtils.sanitizeData(value);
      }
      return sanitized;
    }
    
    return data;
  },

  // Validate email format
  validateEmail: (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  },

  // Validate tracking ID format
  validateTrackingId: (trackingId) => {
    const trackingRegex = /^TK-[A-Z0-9]+$/;
    return trackingRegex.test(trackingId);
  },

  // Check if token is expired
  isTokenExpired: (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }
};

// API methods with enhanced security
export const secureAPI = {
  // Authentication methods
  login: (credentials) => {
    const sanitizedCredentials = securityUtils.sanitizeData(credentials);
    return apiPrivate.post('/auth/login', sanitizedCredentials);
  },

  logout: () => {
    return apiPrivate.post('/auth/logout');
  },

  refreshToken: () => {
    return apiPrivate.post('/auth/refresh');
  },

  // Ticket methods
  createTicket: (ticketData) => {
    const sanitizedData = securityUtils.sanitizeData(ticketData);
    return apiPrivate.post('/tickets', sanitizedData);
  },

  getTicket: (trackingId) => {
    if (!securityUtils.validateTrackingId(trackingId)) {
      return Promise.reject({
        message: 'Invalid tracking ID format',
        status: 400
      });
    }
    
    return apiPrivate.get(`/tickets/track/${trackingId}`);
  },

  updateTicket: (trackingId, updateData) => {
    if (!securityUtils.validateTrackingId(trackingId)) {
      return Promise.reject({
        message: 'Invalid tracking ID format',
        status: 400
      });
    }
    const sanitizedData = securityUtils.sanitizeData(updateData);
    return apiPrivate.put(`/tickets/${trackingId}`, sanitizedData);
  },

  // File upload with progress tracking
  uploadFile: (formData, onProgress) => {
    return apiPrivate.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      }
    });
  },

  // Note: getLocations moved to apiPublic.js for public endpoints

  // Health check
  healthCheck: () => {
    return apiPrivate.get('/health');
  }
};

// Export the private axios instance
export default apiPrivate;

// Export individual methods for convenience
export const {
  login,
  logout,
  refreshToken,
  createTicket,
  getTicket,
  updateTicket,
  uploadFile,
  healthCheck
} = secureAPI;
